import { Table, Button, Grid, Input, Select, Empty } from 'antd';
import type { TableProps, ColumnType } from 'antd/es/table';
import { FilterFilled, SettingOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useColumnSettings } from '@/hooks/useColumnSettings';
import { useAuth } from '@/hooks/useAuth';
import { ColumnSettingsDrawer } from './ColumnSettingsDrawer';
import {
  matchesReportFilter,
  parseReportFilter,
  type ReportFilterKind,
  type ReportFilterOperator,
} from './reportFilters';

const { useBreakpoint } = Grid;

export function ReportTextFilter({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  kind,
}: {
  selectedKeys: React.Key[];
  setSelectedKeys: (keys: React.Key[]) => void;
  confirm: () => void;
  clearFilters?: () => void;
  kind: ReportFilterKind;
}) {
  const parsed = parseReportFilter(selectedKeys[0]);
  const operators: { value: ReportFilterOperator; label: string }[] = kind === 'number'
    ? [
      { value: 'equals', label: 'Bằng' },
      { value: 'gte', label: 'Lớn hơn hoặc bằng' },
      { value: 'lte', label: 'Nhỏ hơn hoặc bằng' },
      { value: 'between', label: 'Trong khoảng' },
    ]
    : kind === 'date'
      ? [
        { value: 'equals', label: 'Bằng' },
        { value: 'between', label: 'Trong khoảng' },
      ]
      : [
        { value: 'contains', label: 'Chứa' },
        { value: 'equals', label: 'Bằng' },
      ];
  // `parseReportFilter` trả 'contains' khi chưa có filter nào — toán tử đó không tồn tại
  // ở cột số/ngày, và nhánh so khớp số sẽ rơi xuống 'between' rồi luôn trả false. Lấy
  // toán tử đầu danh sách của chính kiểu cột thay vì tin giá trị mặc định dùng chung.
  const operator = operators.some((item) => item.value === parsed.operator)
    ? (parsed.operator as ReportFilterOperator)
    : operators[0].value;
  // TCR-1544 — cột số KHÔNG dùng `type="number"`: trình duyệt chỉ nhận một dấu thập phân
  // nên khi kế toán dán "1.187.979.259" copy từ bảng, các dấu chấm nghìn sau bị nuốt và
  // chuỗi rơi về "1.187979259" (tức 1,187979259) → không khớp dòng nào. Ô text giữ nguyên
  // những gì người dùng dán, `parseReportNumber` lo phần hiểu dấu phân cách.
  const valueType = kind === 'date' ? 'date' : 'text';
  const numericProps = kind === 'number' ? { inputMode: 'decimal' as const } : {};
  const setFilter = (next: { operator?: ReportFilterOperator; value?: string; valueTo?: string }) => {
    setSelectedKeys(next.value || next.valueTo ? [JSON.stringify({ operator, ...next })] : []);
  };
  return (
    <div style={{ padding: 8, width: 240 }} onKeyDown={(event) => event.stopPropagation()}>
      <Select
        size="small"
        value={operator}
        options={operators}
        onChange={(next) => setSelectedKeys([JSON.stringify({ operator: next, value: parsed.value, valueTo: parsed.valueTo })])}
        style={{ width: '100%', marginBottom: 8 }}
      />
      <Input
        autoFocus={kind === 'text'}
        type={valueType}
        {...numericProps}
        placeholder="Tìm trong cột"
        value={parsed.value}
        onChange={(event) => setFilter({ value: event.target.value, valueTo: parsed.valueTo })}
        onPressEnter={confirm}
        style={{ marginBottom: 8, display: 'block' }}
      />
      {operator === 'between' && (
        <Input
          type={valueType}
          {...numericProps}
          placeholder="Đến"
          value={parsed.valueTo}
          onChange={(event) => setFilter({ value: parsed.value, valueTo: event.target.value })}
          onPressEnter={confirm}
          style={{ marginBottom: 8, display: 'block' }}
        />
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="primary" size="small" onClick={confirm}>Tìm</Button>
        <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>Xóa lọc</Button>
      </div>
    </div>
  );
}

function inferReportFilterKind<T>(column: ColumnType<T>): ReportFilterKind {
  const title = typeof column.title === 'string' ? column.title.toLowerCase() : '';
  const key = Array.isArray(column.dataIndex) ? column.dataIndex.join('.').toLowerCase() : String(column.dataIndex ?? '').toLowerCase();
  if (/ngày|date|time|thời gian/.test(title) || /date|time/.test(key)) return 'date';
  if (/stt|số thứ tự|số lượng|sl|tiền|giá trị|doanh số|chi phí|công nợ|dư|thuế|chiết khấu|giá vốn|amount|total|quantity|balance/.test(title) || /sequence|index|amount|total|quantity|balance|debit|credit|value/.test(key)) return 'number';
  return 'text';
}

export interface ERPColumnDef<T> extends ColumnType<T> {
  key?: string;
  /** Optional grouped columns for report-style multi-level headers. */
  children?: ERPColumnDef<T>[];
  hideable?: boolean;      // can be hidden by user
  renameable?: boolean;    // can be renamed by user
  alwaysVisible?: boolean; // cannot be hidden
  /**
   * Cột có sẵn trong drawer "Tuỳ chỉnh cột" nhưng khởi tạo ở trạng thái ẨN.
   * Chỉ tác động lần đầu vào trang và sau khi bấm Đặt lại — lựa chọn người dùng đã lưu
   * luôn thắng. Không khai = hiện mặc định (hành vi cũ, mọi cột hiện có giữ nguyên).
   */
  defaultHidden?: boolean;
  hideOnMobile?: boolean;
  /** Plain-text label for column settings when `title` is a ReactNode */
  settingsTitle?: string;
  /**
   * Giá trị thực dùng để lọc. Dùng cho cột có `render` tính toán hoặc cột con
   * (ví dụ số tiền theo từng kỳ trong báo cáo Kết quả kinh doanh).
   */
  filterValue?: (value: unknown, record: T, index?: number) => unknown;
  /**
   * Kiểu dữ liệu của cột, quyết định bộ toán tử của ô lọc đầu cột (Chứa/Bằng cho
   * text; =, ≥, ≤, Trong khoảng cho số) và cách so khớp.
   *
   * Không khai thì `inferReportFilterKind` đoán từ tiêu đề — mà tiêu đề thật hay
   * lệch khỏi từ khoá: "Đơn giá"/"Lãi gộp" là cột số nhưng bị đoán thành text, còn
   * "TK công nợ" là mã tài khoản dạng text lại bị đoán thành số. Cột nào bị đoán
   * sai thì khai thẳng ở đây thay vì nới regex đoán.
   */
  filterKind?: ReportFilterKind;
}

interface ERPTableProps<T> extends Omit<TableProps<T>, 'columns'> {
  tableKey?: string;
  columns: ERPColumnDef<T>[];
  /**
   * Tăng số này khi CỐ Ý đổi bố cục cột mặc định (vd chuyển một cột sang vị trí khác).
   * Người dùng đã lưu layout sẽ nhận thứ tự mới nhưng vẫn giữ hiện/ẩn + tên tự đặt.
   * Không khai = 0 = layout đã lưu của người dùng luôn thắng (hành vi cũ).
   */
  columnsVersion?: number;
  actionColumn?: ColumnType<T>;  // fixed column
  actionColumnPosition?: 'first' | 'last';
  actionColumnFixed?: boolean;
  fitHeight?: boolean;
  bottomOffset?: number;
  /** Extra style for the table wrapper. Needed when the page drives height itself
   *  (fitHeight={false} + a CSS height chain), since a percentage height only
   *  resolves if every ancestor up to the sized container has a height. */
  wrapperStyle?: React.CSSProperties;
  hideSettingsButton?: boolean;
  /** Content rendered on the left side of the settings-button row (e.g. filter
   *  controls). The gear button keeps its place on the right. */
  settingsBarContent?: React.ReactNode;
  registerOpenSettings?: (open: () => void) => void;
  /**
   * Báo các cột ĐANG HIỆN theo đúng thứ tự + tên người dùng đã đặt trong "Tuỳ chỉnh cột",
   * để trang xuất file đúng những gì đang nhìn thấy (TCR-2340). Chỉ gọi khi tập cột đổi.
   */
  onVisibleColumnsChange?: (columns: Array<{ key: string; title: string }>) => void;
  /** Business id accessor used to build row data-testid (defaults to rowKey or _id). */
  testIdAccessor?: (row: T) => string | number | undefined;
}

export function ERPTable<T extends object>({
  tableKey,
  columns,
  columnsVersion = 0,
  actionColumn,
  actionColumnPosition = 'last',
  actionColumnFixed,
  scroll,
  fitHeight = false,
  bottomOffset = 16,
  wrapperStyle,
  hideSettingsButton = false,
  settingsBarContent,
  registerOpenSettings,
  onVisibleColumnsChange,
  testIdAccessor,
  onRow,
  ...tableProps
}: ERPTableProps<T>) {
  const resolvedTableKey = tableKey ?? `erp-table:${typeof window === 'undefined' ? 'report' : window.location.pathname}`;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrollY, setScrollY] = useState<number | undefined>(undefined);
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const shouldFixActionColumn = !isMobile && (actionColumnFixed ?? true);
  const { user } = useAuth();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const openSettingsDrawer = useCallback(() => setDrawerOpen(true), []);

  useEffect(() => {
    if (!fitHeight) return;
    const el = wrapperRef.current;
    if (!el) return;

    const calc = () => {
      const rect = el.getBoundingClientRect();
      // Measure remaining viewport/page space from table top.
      // Do not use the content wrapper's bottom here: when the result is empty,
      // that wrapper can size itself from the empty table and ResizeObserver
      // would feed the smaller height back into the next calculation.
      const available = window.innerHeight - rect.top;
      const thead = el.querySelector<HTMLElement>('.ant-table-thead');
      const pagination = el.querySelector<HTMLElement>('.ant-table-pagination');
      const settings = el.querySelector<HTMLElement>('[data-erp-table-settings]');
      const theadH = thead?.offsetHeight ?? 39;
      const paginationH = pagination?.offsetHeight ?? 0;
      const paginationMargin = paginationH > 0 ? 16 : 0;
      const settingsStyle = settings ? window.getComputedStyle(settings) : null;
      const settingsH = settings
        ? settings.offsetHeight
          + Number.parseFloat(settingsStyle?.marginTop || '0')
          + Number.parseFloat(settingsStyle?.marginBottom || '0')
        : 0;
      const computedHeight = available - settingsH - theadH - paginationH - paginationMargin - bottomOffset;
      const h = computedHeight > 0 ? Math.floor(computedHeight) : undefined;
      setScrollY((prev) => (prev === h ? prev : h));
    };

    const ro = new ResizeObserver(calc);
    ro.observe(el);
    calc();
    window.addEventListener('resize', calc);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', calc);
    };
  }, [fitHeight, bottomOffset, screens.md, screens.lg]);

  useEffect(() => {
    if (!registerOpenSettings) return;
    registerOpenSettings(openSettingsDrawer);
  }, [registerOpenSettings, openSettingsDrawer]);

  // Include every business column by default so list pages do not need to repeat
  // hideable/renameable on each column just to expose it in column settings.
  // `alwaysVisible` remains the opt-out for identity/critical columns.
  const columnKey = (column: ERPColumnDef<T>, index: number) => {
    if (column.key != null) return String(column.key);
    if (Array.isArray(column.dataIndex)) return column.dataIndex.join('.');
    if (column.dataIndex != null) return String(column.dataIndex);
    return `column-${index}`;
  };

  const initialCols = columns
    .filter((c) => !c.alwaysVisible)
    .map((c, index) => ({
      key: columnKey(c, index),
      title: c.settingsTitle
        ?? (typeof c.title === 'string' || typeof c.title === 'number' ? String(c.title) : columnKey(c, index)),
      width: typeof c.width === 'number' ? c.width : undefined,
      defaultHidden: c.defaultHidden,
    }));

  const { settings, setVisible, setDisplayTitle, setWidth, reorder, reset } = useColumnSettings(
    resolvedTableKey,
    initialCols,
    { userId: user?.id, tenantId: user?.tenantId },
    columnsVersion
  );

  const settingMap = useMemo(() => new Map(settings.map((s) => [s.key, s])), [settings]);
  // Vị trí hiển thị lấy từ CHỈ SỐ trong `settings`, không lấy từ số đã lưu trong storage:
  // `settings` luôn được dựng lại theo tập cột hiện tại nên chỉ số này không bao giờ lệch.
  const settingPosition = useMemo(
    () => new Map(settings.map((s, index) => [s.key, index])),
    [settings]
  );
  // Cột `alwaysVisible` ghim tại vị trí source; các cột còn lại hoán vị trong những khe
  // trống giữa chúng. Tính key một lần thay vì `columns.indexOf` trong comparator.
  const keyedColumns = useMemo(
    () => columns.map((col, index) => ({ col, key: columnKey(col, index), baseIndex: index })),
    [columns]
  );
  const configurableSlots = useMemo(
    () => keyedColumns.flatMap(({ col, baseIndex }) => (!col.alwaysVisible ? [baseIndex] : [])),
    [keyedColumns]
  );

  const visibleKeyedColumns = keyedColumns
    .filter(({ col, key }) => {
      if (isMobile && col.hideOnMobile && !col.alwaysVisible) return false;
      if (col.alwaysVisible) return true;
      const s = settingMap.get(key);
      if (!s) return true;
      return s.visible;
    })
    .sort((a, b) => {
      const positionA = settingPosition.get(a.key);
      const positionB = settingPosition.get(b.key);
      const orderA = positionA === undefined ? a.baseIndex : configurableSlots[positionA] ?? a.baseIndex;
      const orderB = positionB === undefined ? b.baseIndex : configurableSlots[positionB] ?? b.baseIndex;
      return orderA - orderB;
    });
  const visibleColumns = visibleKeyedColumns
    .map(({ col, key }) => {
      const s = settingMap.get(key);
      const title = typeof col.title !== 'string' && col.title != null
        ? (s && s.displayTitle !== s.title ? s.displayTitle : col.title)
        : (s?.displayTitle ?? col.title);
      return {
        ...col,
        title,
        width: s?.width ?? col.width,
      };
    });

  const visibleColumnSummary = visibleKeyedColumns.map(({ col, key }) => {
    const s = settingMap.get(key);
    const sourceTitle = typeof col.title === 'string' || typeof col.title === 'number'
      ? String(col.title)
      : col.settingsTitle ?? key;
    return { key, title: s?.displayTitle ?? sourceTitle };
  });
  const visibleColumnSignature = JSON.stringify(visibleColumnSummary);
  const onVisibleColumnsChangeRef = useRef(onVisibleColumnsChange);
  onVisibleColumnsChangeRef.current = onVisibleColumnsChange;
  useEffect(() => {
    onVisibleColumnsChangeRef.current?.(JSON.parse(visibleColumnSignature) as Array<{ key: string; title: string }>);
  }, [visibleColumnSignature]);

  // Report grids use the same in-header filtering convention as AMIS. Existing
  // custom filterDropdown/filterIcon/onFilter definitions always win; this only
  // fills the gap for legacy report columns that had a dataIndex but no filter.
  const isReportRoute = resolvedTableKey.includes('report')
    || (typeof window !== 'undefined' && window.location.pathname.includes('/reports'));
  const addReportFilters = (items: ERPColumnDef<T>[]): ERPColumnDef<T>[] => items.map((column) => {
    const grouped = 'children' in column && Array.isArray(column.children)
      ? { ...column, children: addReportFilters(column.children as ERPColumnDef<T>[]) }
      : column;
    const groupedChildren = 'children' in grouped ? grouped.children : undefined;
    if (!isReportRoute || grouped.filterDropdown || grouped.onFilter || groupedChildren) return grouped;
    if (grouped.dataIndex == null && grouped.filterValue == null) return grouped;
    const dataKey = Array.isArray(grouped.dataIndex)
      ? grouped.dataIndex.join('.')
      : String(grouped.dataIndex ?? grouped.key ?? 'column');
    const filterKind = grouped.filterKind ?? inferReportFilterKind(grouped);
    return {
      ...grouped,
      filterDropdown: ({ selectedKeys, setSelectedKeys, confirm, clearFilters }: any) => (
        <ReportTextFilter
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          kind={filterKind}
        />
      ),
      filterIcon: (filtered: boolean) => <FilterFilled style={{ color: filtered ? '#b0193e' : undefined }} />,
      onFilter: (value: boolean | React.Key, record: T) => {
        const dataValue = Array.isArray(grouped.dataIndex)
          ? grouped.dataIndex.reduce((current: any, key) => current?.[key], record as any)
          : grouped.dataIndex != null ? (record as any)[grouped.dataIndex as string] : undefined;
        const raw = grouped.filterValue ? grouped.filterValue(dataValue, record) : dataValue;
        return matchesReportFilter(raw, value, filterKind);
      },
      key: grouped.key ?? dataKey,
    };
  });

  const filteredColumns = addReportFilters(visibleColumns);

  const actionColObj = actionColumn
    ? (() => {
        const { fixed: _ignoredFixed, ...unpinnedActionColumn } = actionColumn;
        return [
          {
            ...(shouldFixActionColumn ? actionColumn : unpinnedActionColumn),
            ...(shouldFixActionColumn
              ? { fixed: actionColumnPosition === 'first' ? ('left' as const) : ('right' as const) }
              : {}),
            key: '__action',
            width: actionColumn.width ?? (isMobile ? 88 : 120),
          },
        ];
      })()
    : [];

  const finalColumns: ColumnType<T>[] =
    actionColumnPosition === 'first'
      ? [...actionColObj, ...filteredColumns]
      : [...filteredColumns, ...actionColObj];

  const gearBtn = hideSettingsButton ? null : (
    <Button
      size="small"
      title="Tuỳ chỉnh cột"
      aria-label="Tuỳ chỉnh cột"
      icon={<SettingOutlined />}
      onClick={() => setDrawerOpen(true)}
    />
  );

  const settingsBtn = gearBtn == null && settingsBarContent == null ? null : (
    <div
      data-erp-table-settings
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: settingsBarContent != null ? 'space-between' : 'flex-end',
        gap: 8,
        marginBottom: 8,
      }}
    >
      {settingsBarContent != null ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
          {settingsBarContent}
        </div>
      ) : null}
      {gearBtn}
    </div>
  );

  const mergedScroll = useMemo(() => {
    const getColumnWidth = (column: ColumnType<T>): number => {
      const children = 'children' in column && Array.isArray(column.children)
        ? column.children as ColumnType<T>[]
        : undefined;
      if (children && children.length > 0) {
        return children.reduce((sum, child) => sum + getColumnWidth(child), 0);
      }
      return typeof column.width === 'number' ? column.width : 120;
    };
    const totalColWidth = finalColumns.reduce((sum, col) => {
      return sum + getColumnWidth(col);
    }, 0);
    const expandable = tableProps.expandable;
    const expandColumnWidth = expandable
      ? typeof expandable.columnWidth === 'number'
        ? expandable.columnWidth
        : 48
      : 0;
    const baseX = totalColWidth > 0
      ? totalColWidth + expandColumnWidth
      : ((scroll as any)?.x ?? 'max-content');
    const baseY = (scroll as any)?.y;
    return {
      x: baseX,
      ...(fitHeight ? (scrollY != null || baseY != null ? { y: scrollY ?? baseY } : {}) : (baseY != null ? { y: baseY } : {})),
    };
  }, [scroll, fitHeight, scrollY, finalColumns, tableProps.expandable]);

  // Expose the calculated column width to the inner Ant table. Without an
  // explicit min-width, a table with no body rows can collapse its header to
  // the viewport even though every column has a configured width.
  const tableClassName = ['erp-table', tableProps.className].filter(Boolean).join(' ');
  const tableStyle = {
    ...(tableProps.style || {}),
    ...(typeof mergedScroll.x === 'number' ? { '--erp-table-min-width': `${mergedScroll.x}px` } : {}),
  } as React.CSSProperties;

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        flex: fitHeight ? 1 : undefined,
        minHeight: fitHeight ? 0 : undefined,
        ...wrapperStyle,
      }}
    >
      {settingsBtn}
      <Table<T>
        {...tableProps}
        locale={{
          ...tableProps.locale,
          emptyText: (
            <div
              style={
                fitHeight && scrollY
                  ? {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: Math.max(0, scrollY - 40),
                    }
                  : undefined
              }
            >
              {/* `emptyText` của antd nhận cả node lẫn hàm trả node — gọi ra nếu là hàm. */}
              {typeof tableProps.locale?.emptyText === 'function'
                ? tableProps.locale.emptyText()
                : (tableProps.locale?.emptyText ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />)}
            </div>
          ),
        }}
        columns={finalColumns}
        scroll={mergedScroll}
        className={tableClassName}
        rootClassName={tableClassName}
        style={tableStyle}
        sticky={tableProps.sticky ?? true}
        onRow={(record, index) => {
          const base = onRow?.(record, index) ?? {};
          const id =
            testIdAccessor?.(record) ??
            (record as { _id?: string | number; id?: string | number })._id ??
            (record as { id?: string | number }).id;
          return {
            ...base,
            'data-testid': id != null ? `${resolvedTableKey}-row-${id}` : `${resolvedTableKey}-row-${index}`,
          } as React.HTMLAttributes<HTMLElement>;
        }}
      />
      <ColumnSettingsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        settings={settings}
        onSetVisible={setVisible}
        onSetDisplayTitle={setDisplayTitle}
        onSetWidth={setWidth}
        onReorder={reorder}
        onReset={reset}
      />
    </div>
  );
}

// Compatibility surface for legacy report footers while migrating tables to ERPTable.
export namespace ERPTable {
  export const Summary = Table.Summary;
}
